import React from 'react';
import { Plus, X, AlertTriangle } from 'lucide-react';
import { Field, Input, Select, Textarea, UnitInput, Segmented } from '../ui/Field';
import {
    BillingSettings,
    CatalogItem,
    CRMContactSuggestion,
    InvoiceLine,
    InvoiceLineSummary,
    getCRMDisplayName,
    prixUnitaireHt,
    toCurrency,
} from './invoiceTypes';

/**
 * La colonne de saisie de l'écran de création (maquette 8b) : quatre sections nommées — la facture,
 * le client, les lignes, le règlement.
 *
 * Champs et calculs inchangés : numéro automatique, autocomplétion CRM qui remplit adresse, SIRET et
 * TVA, bascule de saisie TTC / HT, montants saisis taux par taux, catalogue et éléments facturables
 * issus des commandes et des renforts, montant déjà réglé.
 */

const SectionTitle = ({ children, aside }: { children: React.ReactNode; aside?: React.ReactNode }) => (
    <div className="flex items-center justify-between gap-3 flex-wrap pb-2.5 mb-3.5 border-b border-border">
        <span className="text-[10px] font-bold uppercase tracking-[.09em] text-content-muted">{children}</span>
        {aside}
    </div>
);

export interface InvoiceFormProps {
    settings: BillingSettings;
    user: any;
    /** Établissement de type Prestation : seul concerné par les champs de facture électronique. */
    isPrestation: boolean;
    /** PARTICULIER ou PROFESSIONNEL — déduit de la fiche CRM choisie. Détermine si l'avertissement
     *  facturation électronique ci-dessous s'affiche (l'obligation ne concerne que le B2B). */
    clientType: string;

    invoiceNumber: string;
    setInvoiceNumber: (v: string) => void;
    invoiceDate: string;
    setInvoiceDate: (v: string) => void;

    operationNature: string;
    setOperationNature: (v: string) => void;
    purchaseOrderRef: string;
    setPurchaseOrderRef: (v: string) => void;
    deliveryAddress: string;
    setDeliveryAddress: (v: string) => void;

    clientName: string;
    onClientNameChange: (v: string) => void;
    selectedCrmContactId: string;
    recipientEmail: string;
    showCrmSuggestions: boolean;
    setShowCrmSuggestions: (v: boolean) => void;
    crmSearchLoading: boolean;
    crmSuggestions: CRMContactSuggestion[];
    applyCRMContact: (contact: CRMContactSuggestion) => void;

    clientAddress: string;
    setClientAddress: (v: string) => void;
    clientCity: string;
    setClientCity: (v: string) => void;
    clientPostalCode: string;
    setClientPostalCode: (v: string) => void;
    clientCountry: string;
    setClientCountry: (v: string) => void;
    coverCount: string;
    setCoverCount: (v: string) => void;

    amountMode: 'ttc' | 'ht';
    setAmountMode: (v: 'ttc' | 'ht') => void;
    lines: InvoiceLine[];
    lineSummaries: InvoiceLineSummary[];
    tvaRates: number[];
    catalog: CatalogItem[];
    billableItems: { id: string; source: 'commande' | 'extra'; label: string; amount: number }[];
    addLine: () => void;
    removeLine: (lineId: string) => void;
    updateLine: (lineId: string, patch: Partial<InvoiceLine>) => void;
    updateLineRate: (lineId: string, rate: number, value: string) => void;

    amountAlreadyPaid: string;
    setAmountAlreadyPaid: (v: string) => void;
}

export const InvoiceForm: React.FC<InvoiceFormProps> = ({
    settings, user, isPrestation, clientType,
    invoiceNumber, setInvoiceNumber, invoiceDate, setInvoiceDate,
    operationNature, setOperationNature, purchaseOrderRef, setPurchaseOrderRef, deliveryAddress, setDeliveryAddress,
    clientName, onClientNameChange, selectedCrmContactId, recipientEmail,
    showCrmSuggestions, setShowCrmSuggestions, crmSearchLoading, crmSuggestions, applyCRMContact,
    clientAddress, setClientAddress, clientCity, setClientCity, clientPostalCode, setClientPostalCode,
    clientCountry, setClientCountry, coverCount, setCoverCount,
    amountMode, setAmountMode, lines, lineSummaries, tvaRates, catalog, billableItems,
    addLine, removeLine, updateLine, updateLineRate,
    amountAlreadyPaid, setAmountAlreadyPaid,
}) => {
    // Le numéro n'est modifiable que par le super admin : il est calculé à partir des factures déjà
    // émises et deux factures ne peuvent pas porter le même.
    const numberIsReadOnly = user?.type !== 'admin' && !user?.impersonatedBySuperAdmin;

    // Une colonne de saisie par taux de TVA : la saisie se fait taux par taux, parce qu'une même
    // ligne peut porter deux taux (10 % sur le repas, 20 % sur la boisson).
    const lineColumns = `minmax(140px,2.2fr) 54px ${tvaRates.map(() => 'minmax(82px,.95fr)').join(' ')} minmax(70px,.7fr) minmax(74px,.7fr) 24px`;

    // LIGNE LIBRE (2026-09-21, demande d'Emeni) : « si par exemple on facture quelque chose qu'on ne
    // propose pas dans nos prestations ». Le libellé était enfermé dans une liste déroulante — les
    // montants, eux, étaient déjà libres. On garde la liste (catalogue, commandes, extras) et on ajoute
    // un choix « Autre » qui bascule la cellule en saisie libre.
    // Une facture rouverte dont le libellé n'est plus dans aucune liste s'ouvre directement en saisie
    // libre : c'est forcément une ligne écrite à la main, ou un article retiré du catalogue depuis.
    const [lignesLibres, setLignesLibres] = React.useState<Record<string, boolean>>({});
    const estLibre = (line: InvoiceLine) => {
        if (lignesLibres[line.id]) return true;
        if (!line.label) return false;
        return !catalog.some((c) => c.label === line.label) && !billableItems.some((b) => b.label === line.label);
    };

    return (
        <div className="rounded-2xl border border-border bg-surface p-[18px] field-dense">
            <SectionTitle>La facture</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-5">
                <Field
                    label="Numéro"
                    help={numberIsReadOnly ? 'Numéroté automatiquement.' : undefined}
                    disabled={numberIsReadOnly}
                >
                    {({ id, describedBy }) => (
                        <Input
                            id={id}
                            aria-describedby={describedBy}
                            className="font-mono text-[12.5px]"
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            readOnly={numberIsReadOnly}
                            disabled={numberIsReadOnly}
                        />
                    )}
                </Field>
                <Field label="Date">
                    {({ id }) => (
                        <Input id={id} type="date" className="tabular-nums" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
                    )}
                </Field>
            </div>

            {/* Données exigées par la facture électronique (norme EN 16931), affichées uniquement pour
                les établissements de type Prestation — seuls concernés par l'émission obligatoire
                entre professionnels. */}
            {isPrestation && (
                <>
                    <SectionTitle>Facturation électronique</SectionTitle>
                    {/* Règle métier ajoutée le 2026-09-24 : la facture électronique n'est obligatoire
                        qu'entre professionnels (jamais pour un particulier). Simple avertissement, pas de
                        blocage — l'obligation d'émission n'entre en vigueur pour les PME/TPE/micro-entreprises
                        que le 1er septembre 2027 ; d'ici là, la facture actuelle reste valable. */}
                    {clientType === 'PROFESSIONNEL' && (
                        <div className="flex gap-2.5 p-3 mb-3.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400">
                            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                            <p className="text-xs leading-relaxed">
                                Facture à un client professionnel : à partir du 1<sup>er</sup> septembre 2027, elle devra être
                                transmise via une plateforme agréée par l'État. Configurez-la dans Paramètres&nbsp;&gt; Facturation
                                électronique dès que possible.
                            </p>
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-2.5">
                        <Field label="Nature de l'opération">
                            {({ id }) => (
                                <Select id={id} value={operationNature} onChange={(e) => setOperationNature(e.target.value)}>
                                    <option value="services">Prestation de services</option>
                                    <option value="biens">Livraison de biens</option>
                                    <option value="mixte">Les deux</option>
                                </Select>
                            )}
                        </Field>
                        <Field label="Réf. bon de commande">
                            {({ id }) => (
                                <Input id={id} value={purchaseOrderRef} onChange={(e) => setPurchaseOrderRef(e.target.value)} placeholder="Facultatif" />
                            )}
                        </Field>
                    </div>
                    <Field
                        label="Adresse de livraison, si différente"
                        // Correction 2026-09-24 : la nature de l'opération EST une mention obligatoire à
                        // imprimer depuis la réforme (service-public.fr, "4 nouvelles mentions obligatoires"),
                        // contrairement à ce que disait ce texte d'aide — elle s'affiche désormais sur le
                        // document (voir InvoiceDocument.tsx / invoicePdfGenerator.ts).
                        help="La référence de commande est souvent exigée par les grands comptes pour payer. La nature de l'opération s'imprime sur la facture."
                        className="mb-5"
                    >
                        {({ id, describedBy }) => (
                            <Input id={id} aria-describedby={describedBy} value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} placeholder="Facultatif" />
                        )}
                    </Field>
                </>
            )}

            <SectionTitle>Le client</SectionTitle>
            <div className="relative mb-2.5">
                <Field
                    label="Nom de l'entreprise ou du particulier"
                    help="Choisir un contact remplit l'adresse, le SIRET et la TVA."
                >
                    {({ id, describedBy }) => (
                        <Input
                            id={id}
                            aria-describedby={describedBy}
                            value={clientName}
                            onChange={(e) => onClientNameChange(e.target.value)}
                            onFocus={() => setShowCrmSuggestions(true)}
                            onBlur={() => window.setTimeout(() => setShowCrmSuggestions(false), 150)}
                            placeholder="Tapez les premières lettres d’un client CRM"
                            autoComplete="off"
                        />
                    )}
                </Field>
                {showCrmSuggestions && clientName.trim().length >= 2 && (
                    <div className="absolute top-full left-0 right-0 -mt-4 z-20 rounded-[10px] border border-border bg-surface shadow-xl overflow-hidden">
                        {crmSearchLoading ? (
                            <div className="px-3 py-2 text-[12px] text-content-muted">Recherche CRM…</div>
                        ) : crmSuggestions.length === 0 ? (
                            <div className="px-3 py-2 text-[12px] text-content-muted">Aucun client CRM correspondant</div>
                        ) : crmSuggestions.map((contact) => (
                            <button
                                key={contact.id}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => applyCRMContact(contact)}
                                className="w-full text-left px-3 py-2 hover:bg-surface-soft transition-colors border-b border-border last:border-b-0"
                            >
                                <span className="block text-[12.5px] font-semibold text-content">{getCRMDisplayName(contact)}</span>
                                <span className="block text-[11px] text-content-muted truncate">
                                    {[contact.address, contact.postal_code, contact.city].filter(Boolean).join(', ') || contact.email || 'Client CRM'}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {(selectedCrmContactId || recipientEmail) && (
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {selectedCrmContactId && (
                        <span className="inline-flex items-center h-[22px] px-2 rounded-full text-[10.5px] font-bold whitespace-nowrap shrink-0" style={{ background: 'var(--st-ok-bg)', color: 'var(--st-ok-fg)' }}>Client CRM lié</span>
                    )}
                    {recipientEmail && (
                        <span className="inline-flex items-center h-[22px] px-2 rounded-full text-[10.5px] font-bold whitespace-nowrap shrink-0 max-w-full truncate" style={{ background: 'var(--st-info-bg)', color: 'var(--st-info-fg)' }}>{recipientEmail}</span>
                    )}
                </div>
            )}

            <Field label="Adresse complète" className="mb-2.5">
                {({ id }) => (
                    <Textarea id={id} rows={3} value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} />
                )}
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
                <Field label="Ville">
                    {({ id }) => <Input id={id} value={clientCity} onChange={(e) => setClientCity(e.target.value)} />}
                </Field>
                <Field label="Code postal">
                    {({ id }) => <Input id={id} className="tabular-nums" value={clientPostalCode} onChange={(e) => setClientPostalCode(e.target.value)} />}
                </Field>
                <Field label="Pays">
                    {({ id }) => <Input id={id} value={clientCountry} onChange={(e) => setClientCountry(e.target.value)} />}
                </Field>
            </div>

            <SectionTitle
                aside={(
                    <span className="flex items-center gap-2">
                        <span className="text-[11px] text-content-muted">Saisie</span>
                        <Segmented
                            ariaLabel="Saisir les montants en TTC ou en HT"
                            value={amountMode}
                            onChange={(v) => setAmountMode(v as 'ttc' | 'ht')}
                            options={[{ value: 'ttc', label: 'TTC' }, { value: 'ht', label: 'HT' }]}
                        />
                    </span>
                )}
            >
                Lignes
            </SectionTitle>

            <div className="overflow-x-auto scroll-slim -mx-[18px] px-[18px] pb-1 mb-2.5">
                <div className="min-w-[470px] border border-border rounded-[10px] overflow-hidden">
                    <div className="grid gap-1.5 px-2.5 py-2 bg-surface-soft border-b border-border" style={{ gridTemplateColumns: lineColumns }}>
                        <span className="text-[9.5px] font-bold uppercase tracking-[.07em] text-content-muted">Description</span>
                        <span className="text-[9.5px] font-bold uppercase tracking-[.07em] text-content-muted text-center">Qté</span>
                        {tvaRates.map((rate) => (
                            <span key={rate} className="text-[9.5px] font-bold uppercase tracking-[.07em] text-content-muted text-right">{amountMode === 'ttc' ? 'TTC' : 'HT'} {rate} %</span>
                        ))}
                        <span className="text-[9.5px] font-bold uppercase tracking-[.07em] text-content-muted text-right">P.U. HT</span>
                        <span className="text-[9.5px] font-bold uppercase tracking-[.07em] text-content-muted text-right">Total TTC</span>
                        <span />
                    </div>

                    {lines.map((line, index) => {
                        const summary = lineSummaries[index];
                        return (
                            <div
                                key={line.id}
                                className="grid gap-1.5 items-center px-2.5 py-1.5 border-b border-border/60 last:border-0"
                                style={{ gridTemplateColumns: lineColumns }}
                            >
                                {estLibre(line) ? (
                                    <div className="flex items-center gap-1">
                                        <Input
                                            aria-label={`Libellé libre de la ligne ${index + 1}`}
                                            value={line.label}
                                            onChange={(e) => updateLine(line.id, { label: e.target.value })}
                                            placeholder="Ce que vous facturez"
                                            className="!h-8 !text-[12px]"
                                        />
                                        <button
                                            type="button"
                                            title="Revenir à la liste des prestations"
                                            onClick={() => { setLignesLibres((prev) => ({ ...prev, [line.id]: false })); updateLine(line.id, { label: '' }); }}
                                            className="shrink-0 px-1.5 h-8 text-[11px] font-bold text-content-muted hover:text-content"
                                        >
                                            ↩
                                        </button>
                                    </div>
                                ) : (
                                <Select
                                    aria-label={`Prestation de la ligne ${index + 1}`}
                                    value={line.label}
                                    onChange={(e) => {
                                        if (e.target.value === '__libre__') {
                                            setLignesLibres((prev) => ({ ...prev, [line.id]: true }));
                                            updateLine(line.id, { label: '' });
                                            return;
                                        }
                                        updateLine(line.id, { label: e.target.value });
                                    }}
                                    className="!h-8 !text-[12px]"
                                >
                                    {catalog.length === 0 && billableItems.length === 0 && <option value="">Aucune prestation configurée</option>}
                                    {(catalog.length > 0 || billableItems.length > 0) && !catalog.some((item) => item.label === line.label) && !billableItems.some((item) => item.label === line.label) && (
                                        <option value={line.label}>{line.label || 'Sélectionner une prestation'}</option>
                                    )}
                                    {catalog.map((item) => (
                                        <option key={item.id} value={item.label}>{item.label}</option>
                                    ))}
                                    {billableItems.some((i) => i.source === 'commande') && (
                                        <optgroup label="Commandes fournisseur">
                                            {billableItems.filter((i) => i.source === 'commande').map((item) => (
                                                <option key={item.id} value={item.label}>{item.label}</option>
                                            ))}
                                        </optgroup>
                                    )}
                                    {billableItems.some((i) => i.source === 'extra') && (
                                        <optgroup label="Extras (sans prix — à saisir)">
                                            {billableItems.filter((i) => i.source === 'extra').map((item) => (
                                                <option key={item.id} value={item.label}>{item.label}</option>
                                            ))}
                                        </optgroup>
                                    )}
                                    <option value="__libre__">✏️ Autre — saisir un libellé…</option>
                                </Select>
                                )}

                                <Input
                                    aria-label={`Quantité de la ligne ${index + 1}`}
                                    type="number"
                                    min={1}
                                    value={line.quantity}
                                    onChange={(e) => updateLine(line.id, { quantity: Math.max(1, Number(e.target.value || 1)) })}
                                    className="!h-8 !text-[12px] text-center tabular-nums !px-1.5"
                                />

                                {tvaRates.map((rate) => (
                                    <Input
                                        key={rate}
                                        aria-label={`Montant ${amountMode === 'ttc' ? 'TTC' : 'HT'} à ${rate} % de la ligne ${index + 1}`}
                                        type="number"
                                        step="0.01"
                                        min={0}
                                        value={amountMode === 'ttc' ? (line.ttcByRate[rate] ?? 0) : Number(((line.ttcByRate[rate] ?? 0) / (1 + rate / 100)).toFixed(2))}
                                        onChange={(e) => updateLineRate(line.id, rate, e.target.value)}
                                        className="!h-8 !text-[12px] text-right tabular-nums !px-2"
                                    />
                                ))}

                                <span className="text-[12px] text-content-muted text-right tabular-nums">{summary ? prixUnitaireHt(summary) : '-'}</span>
                                <span className="text-[12px] font-semibold text-content text-right tabular-nums">{summary ? toCurrency(summary.totalTtc) : '-'}</span>

                                <button
                                    type="button"
                                    onClick={() => removeLine(line.id)}
                                    title="Retirer cette ligne"
                                    aria-label={`Retirer la ligne ${index + 1}`}
                                    className="w-6 h-6 flex items-center justify-center rounded-md text-[#8f5450] hover:bg-state-danger-soft hover:text-state-danger transition-colors shrink-0"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            <button
                type="button"
                onClick={addLine}
                className="h-8 px-3 mb-5 inline-flex items-center gap-1.5 rounded-full border border-dashed border-border text-[12px] font-semibold text-content-muted transition-colors hover:border-content whitespace-nowrap shrink-0"
            >
                <Plus size={13} /> Ajouter une ligne
            </button>

            <SectionTitle>Règlement</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {settings.enable_cover_count && (
                    <Field label="Couverts">
                        {({ id }) => (
                            <Input id={id} type="number" min={0} className="tabular-nums" value={coverCount} onChange={(e) => setCoverCount(e.target.value)} placeholder="Ex : 300" />
                        )}
                    </Field>
                )}
                <Field label="Déjà réglé" help="Acompte ou règlement partiel déjà effectué par le client.">
                    {({ id, describedBy }) => (
                        <UnitInput
                            id={id}
                            aria-describedby={describedBy}
                            unit="€"
                            type="number"
                            step="0.01"
                            min={0}
                            value={amountAlreadyPaid}
                            onChange={(e) => setAmountAlreadyPaid(e.target.value)}
                            placeholder="0,00"
                        />
                    )}
                </Field>
            </div>
        </div>
    );
};
